/**
 * Estado e regras do assistente "Nova empresa" (7 etapas), sem JSX.
 *
 * Decisoes nao obvias:
 *  1. Dinheiro e percentual entram como TEXTO no campo ("49,70", "15,00") e
 *     viram inteiro (centavos, bps) por conta de STRING -- parte inteira e
 *     parte decimal separadas e concatenadas -- nunca por `parseFloat * 100`.
 *     49,70 * 100 em float da 4969.999...; como inteiro, e 4970 sempre.
 *  2. CNPJ e telefone ficam guardados so com DIGITOS; a mascara e aplicada
 *     na exibicao. Guardar o texto mascarado obrigaria a limpar antes de
 *     validar e antes de mandar ao servidor, dois lugares para errar.
 *  3. A validacao e POR ETAPA e devolve um mapa campo -> mensagem. A pagina
 *     so mostra os erros depois da primeira tentativa de avancar naquela
 *     etapa: campo vermelho antes de a pessoa digitar e ruido.
 *  4. Lista de provedores e de funis sempre na ORDEM canonica (`PROVEDORES`,
 *     `FUNIS`), independente da ordem dos cliques, para a revisao e as URLs
 *     sairem iguais em qualquer sequencia de preenchimento.
 *  5. O GENERICO (checkout proprio) entra na lista de provedores do formulario
 *     e gera URL de webhook, mas NAO vai para `CompanySummary.providers`: o tipo
 *     compartilhado ainda so conhece os tres dedicados (pedido no relatorio).
 *  6. Politica de estorno nasce no padrao decidido (`reverte_chargeback`:
 *     reembolso nao reverte, chargeback reverte) e pode ser trocada por empresa.
 */

import type { StepItem } from '../../components/ui';
import type { BillingModel, CompanySummary, FunnelKind, LogoTone, Provider, RefundPolicy } from '../../types';
import { iniciaisDe, slugDe, urlDoWebhook } from './identidade';
import type { ProvedorIntegracao } from './types';
import { FUNIS, POLITICA_PADRAO, PROVEDORES, emailValido, siteValido } from './vocabulario';

export const ETAPAS: readonly StepItem[] = [
  { key: 'dados', label: 'Dados', hint: 'Nome, CNPJ e contato' },
  { key: 'marca', label: 'Marca', hint: 'Logo e cor' },
  { key: 'comissao', label: 'Comissão e cobrança', hint: 'Percentual, cobrança e estornos' },
  { key: 'ofertas', label: 'Ofertas', hint: 'Produtos e planos' },
  { key: 'integracoes', label: 'Integrações', hint: 'Webhooks, WhatsApp e voz' },
  { key: 'funis', label: 'Funis', hint: 'Régua padrão' },
  { key: 'acesso', label: 'Acesso e revisão', hint: 'Dono e conferência' },
];

export const ULTIMA_ETAPA = ETAPAS.length - 1;

export interface OfertaRascunho {
  readonly id: string;
  readonly nome: string;
  readonly precoCents: number;
  readonly recorrente: boolean;
  readonly provider: ProvedorIntegracao;
}

export interface FormNovaEmpresa {
  // 1. Dados
  readonly nome: string;
  readonly segmento: string;
  /** So digitos (ate 14). */
  readonly cnpj: string;
  readonly site: string;
  readonly contatoNome: string;
  readonly contatoEmail: string;
  /** So digitos (10 ou 11, com DDD). */
  readonly contatoTelefone: string;
  // 2. Marca
  readonly logoDataUrl: string | null;
  readonly logoNomeArquivo: string | null;
  readonly logoTone: LogoTone;
  // 3. Comissao e cobranca
  readonly feeBps: number;
  readonly billingModel: BillingModel;
  readonly refundPolicy: RefundPolicy;
  // 4. Ofertas
  readonly ofertas: readonly OfertaRascunho[];
  // 5. Integracoes
  readonly provedores: readonly ProvedorIntegracao[];
  readonly segredos: Readonly<Record<ProvedorIntegracao, string>>;
  readonly evolutionInstancia: string;
  readonly vapiAssistente: string;
  // 6. Funis
  readonly funis: readonly FunnelKind[];
  // 7. Acesso
  readonly donoNome: string;
  readonly donoEmail: string;
  readonly enviarConvite: boolean;
}

export const FORM_INICIAL: FormNovaEmpresa = {
  nome: '',
  segmento: '',
  cnpj: '',
  site: '',
  contatoNome: '',
  contatoEmail: '',
  contatoTelefone: '',
  logoDataUrl: null,
  logoNomeArquivo: null,
  logoTone: 1,
  feeBps: 1_500,
  billingModel: 'a_definir',
  refundPolicy: POLITICA_PADRAO,
  ofertas: [],
  provedores: [],
  segredos: { kiwify: '', hotmart: '', shopify: '', generic: '', hubla: '', eduzz: '', cakto: '', greenn: '' },
  evolutionInstancia: '',
  vapiAssistente: '',
  funis: ['carrinho_abandonado'],
  donoNome: '',
  donoEmail: '',
  enviarConvite: true,
};

export const FEE_MIN_BPS = 100;
export const FEE_MAX_BPS = 5_000;

// -----------------------------------------------------------------------------
// Validacao
// -----------------------------------------------------------------------------

export type Erros = Readonly<Record<string, string>>;

export function validarEtapa(etapa: number, form: FormNovaEmpresa): Erros {
  const erros: Record<string, string> = {};
  switch (etapa) {
    case 0:
      if (form.nome.trim() === '') {
        erros['nome'] = 'Informe o nome da empresa.';
      }
      if (form.segmento.trim() === '') {
        erros['segmento'] = 'Informe o segmento.';
      }
      if (form.cnpj !== '' && form.cnpj.length !== 14) {
        erros['cnpj'] = 'CNPJ precisa ter 14 dígitos.';
      }
      if (!siteValido(form.site)) {
        erros['site'] = 'Informe um endereço válido, como loja.exemplo.';
      }
      if (form.contatoNome.trim() === '') {
        erros['contatoNome'] = 'Informe quem é o contato.';
      }
      if (!emailValido(form.contatoEmail)) {
        erros['contatoEmail'] = 'Informe um e-mail válido.';
      }
      if (form.contatoTelefone !== '' && (form.contatoTelefone.length < 10 || form.contatoTelefone.length > 11)) {
        erros['contatoTelefone'] = 'Telefone com DDD: 10 ou 11 dígitos.';
      }
      break;
    case 1:
      break;
    case 2:
      if (form.feeBps < FEE_MIN_BPS || form.feeBps > FEE_MAX_BPS) {
        erros['feeBps'] = 'Percentual entre 1,00% e 50,00%.';
      }
      break;
    case 3:
      if (form.ofertas.length === 0) {
        erros['ofertas'] = 'Cadastre ao menos uma oferta.';
      }
      break;
    case 4:
      if (form.provedores.length === 0) {
        erros['provedores'] = 'Marque ao menos um provedor de checkout.';
      }
      if (form.evolutionInstancia.trim() === '') {
        erros['evolutionInstancia'] = 'Informe o nome da instância da Evolution.';
      }
      if (form.vapiAssistente.trim() === '') {
        erros['vapiAssistente'] = 'Informe o nome do assistente de voz.';
      }
      break;
    case 5:
      if (form.funis.length === 0) {
        erros['funis'] = 'Ative ao menos um funil.';
      }
      break;
    case 6:
      if (form.donoNome.trim() === '') {
        erros['donoNome'] = 'Informe o nome do dono da conta.';
      }
      if (!emailValido(form.donoEmail)) {
        erros['donoEmail'] = 'Informe um e-mail válido.';
      }
      break;
    default:
      break;
  }
  return erros;
}

export function etapaValida(etapa: number, form: FormNovaEmpresa): boolean {
  return Object.keys(validarEtapa(etapa, form)).length === 0;
}

export function primeiraEtapaInvalida(form: FormNovaEmpresa): number | null {
  for (let etapa = 0; etapa <= ULTIMA_ETAPA; etapa += 1) {
    if (!etapaValida(etapa, form)) {
      return etapa;
    }
  }
  return null;
}

// -----------------------------------------------------------------------------
// Mascaras (exibicao) e conversoes de texto de campo para inteiro
// -----------------------------------------------------------------------------

export function somenteDigitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

/** 14 digitos -> "00.000.000/0000-00" (parcial enquanto digita). */
export function mascararCnpj(digitos: string): string {
  const d = digitos.slice(0, 14);
  const partes = [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8), d.slice(8, 12), d.slice(12, 14)];
  let saida = partes[0] ?? '';
  if ((partes[1] ?? '') !== '') {
    saida += `.${partes[1]}`;
  }
  if ((partes[2] ?? '') !== '') {
    saida += `.${partes[2]}`;
  }
  if ((partes[3] ?? '') !== '') {
    saida += `/${partes[3]}`;
  }
  if ((partes[4] ?? '') !== '') {
    saida += `-${partes[4]}`;
  }
  return saida;
}

/** 10 ou 11 digitos -> "(11) 99999-9999" (parcial enquanto digita). */
export function mascararTelefone(digitos: string): string {
  const d = digitos.slice(0, 11);
  if (d.length <= 2) {
    return d === '' ? '' : `(${d}`;
  }
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  if (resto.length <= 4) {
    return `(${ddd}) ${resto}`;
  }
  const corte = resto.length > 8 ? 5 : 4;
  return `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`;
}

/** Digitos com DDD -> E.164 brasileiro; `null` se incompleto. */
export function telefoneE164(digitos: string): string | null {
  return digitos.length === 10 || digitos.length === 11 ? `+55${digitos}` : null;
}

const REAIS_RE = /^(\d{1,3}(?:\.\d{3})*|\d+)(?:[,.](\d{1,2}))?$/;

/** "1.299,00" | "1299" | "49.90" -> centavos inteiros; `null` se nao for um valor. */
export function reaisParaCentavos(texto: string): number | null {
  const limpo = texto.trim().replace(/\s|R\$/g, '');
  const m = REAIS_RE.exec(limpo);
  if (m === null) {
    return null;
  }
  const inteiro = (m[1] ?? '0').replace(/\./g, '');
  const decimal = (m[2] ?? '').padEnd(2, '0');
  return Number(inteiro) * 100 + Number(decimal);
}

/** Centavos -> "49,70" para preencher um campo de texto (nao e formatador de exibicao). */
export function centavosParaTexto(cents: number): string {
  return `${Math.floor(cents / 100)},${String(cents % 100).padStart(2, '0')}`;
}

const PERCENT_RE = /^(\d{1,2})(?:[,.](\d{1,2}))?$/;

/** "15" | "15,5" | "15,00" -> bps inteiros; `null` se nao for um percentual. */
export function textoParaBps(texto: string): number | null {
  const m = PERCENT_RE.exec(texto.trim().replace('%', ''));
  if (m === null) {
    return null;
  }
  const inteiro = m[1] ?? '0';
  const decimal = (m[2] ?? '').padEnd(2, '0');
  return Number(inteiro) * 100 + Number(decimal);
}

/** bps -> "15,00" para o campo de texto. */
export function bpsParaTexto(bps: number): string {
  return `${Math.floor(bps / 100)},${String(bps % 100).padStart(2, '0')}`;
}

// -----------------------------------------------------------------------------
// Derivacoes do formulario
// -----------------------------------------------------------------------------

export function ordenarProvedores(lista: readonly ProvedorIntegracao[]): readonly ProvedorIntegracao[] {
  return PROVEDORES.filter((p) => lista.includes(p));
}

export function ordenarFunis(lista: readonly FunnelKind[]): readonly FunnelKind[] {
  return FUNIS.filter((f) => lista.includes(f));
}

/** Provedores que as ofertas usam, na ordem canonica. */
export function provedoresDasOfertas(form: FormNovaEmpresa): readonly ProvedorIntegracao[] {
  return PROVEDORES.filter((p) => form.ofertas.some((o) => o.provider === p));
}

/**
 * Provedor com adaptador proprio (todos menos o generico). Na previa, a
 * empresa recem-criada guarda so estes em `providers`: o generico da previa e
 * decidido pelo gerador simulado (admin-empresas/mock.ts), como sempre foi.
 */
export function ehProviderDedicado(p: ProvedorIntegracao): p is Exclude<Provider, 'generic'> {
  return p !== 'generic';
}

export function temOfertaRecorrente(form: FormNovaEmpresa): boolean {
  return form.ofertas.some((o) => o.recorrente);
}

export function sugerirInstancia(nome: string): string {
  const slug = slugDe(nome);
  return slug === '' ? '' : `cf-${slug}`;
}

export function sugerirAssistente(nome: string): string {
  return nome.trim() === '' ? '' : `Assistente ${nome.trim()}`;
}

// -----------------------------------------------------------------------------
// Resultado da criacao (previa)
// -----------------------------------------------------------------------------

export interface EmpresaCriada {
  readonly company: CompanySummary;
  readonly webhooks: readonly { readonly provider: ProvedorIntegracao; readonly url: string }[];
  readonly ofertas: readonly OfertaRascunho[];
  readonly dono: { readonly nome: string; readonly email: string; readonly convite: boolean };
  readonly evolutionInstancia: string;
  readonly vapiAssistente: string;
}

/** Slug previsto para a empresa; "nova-empresa" enquanto o nome nao foi digitado. */
export function slugPrevisto(form: FormNovaEmpresa): string {
  const slug = slugDe(form.nome.trim());
  return slug === '' ? 'nova-empresa' : slug;
}

/** Id que a empresa vai ter na previa. A URL de webhook da etapa 5 ja nasce com ele. */
export function idPrevisto(form: FormNovaEmpresa): string {
  return `SIMULADO-empresa-${slugPrevisto(form)}`;
}

export function montarEmpresaCriada(form: FormNovaEmpresa, criadaEmIso: string): EmpresaCriada {
  const nome = form.nome.trim();
  const slug = slugPrevisto(form);
  const id = idPrevisto(form);
  const company: CompanySummary = {
    id,
    name: nome,
    slug,
    segment: form.segmento.trim(),
    logoInitials: iniciaisDe(nome),
    logoTone: form.logoTone,
    logoUrl: form.logoDataUrl,
    feeBps: form.feeBps,
    billingModel: form.billingModel,
    refundPolicy: form.refundPolicy,
    status: 'onboarding',
    providers: ordenarProvedores(form.provedores).filter(ehProviderDedicado),
    funnels: ordenarFunis(form.funis),
    contactName: form.contatoNome.trim(),
    contactEmail: form.contatoEmail.trim().toLowerCase(),
    createdAt: criadaEmIso,
  };
  return {
    company,
    webhooks: ordenarProvedores(form.provedores).map((provider) => ({ provider, url: urlDoWebhook(provider, id) })),
    ofertas: form.ofertas,
    dono: { nome: form.donoNome.trim(), email: form.donoEmail.trim().toLowerCase(), convite: form.enviarConvite },
    evolutionInstancia: form.evolutionInstancia.trim(),
    vapiAssistente: form.vapiAssistente.trim(),
  };
}
